'use client';

import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'next/navigation';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { ProductCategoryNavigationMobileV2FragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductCategoryNavigationMobileV2FragmentFragmentDoc';
import type { ProductCategoryNavigationMobileWorkaroundV2FragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductCategoryNavigationMobileWorkaroundV2FragmentFragmentDoc';
import type { UseCategoryDataForProductCategoryNavigationMobileV2FragmentFragmentDoc } from '@packages/gql/generated/shopping/UseCategoryDataForProductCategoryNavigationMobileV2FragmentFragmentDoc';
import type { UseCategoryDataForProductCategoryNavigationMobileWorkaroundV2FragmentFragmentDoc } from '@packages/gql/generated/shopping/UseCategoryDataForProductCategoryNavigationMobileWorkaroundV2FragmentFragmentDoc';
import { useRouterPush } from '@packages/shared/src/hooks/useRouterPush/useRouterPush';
import { searchParamsToObject } from '@packages/shared/src/utils/searchParamsToObject/searchParamsToObject';
import { mergeQueryParameters } from '@packages/shared/src/utils/mergeQueryParameters/mergeQueryParameters';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Radio } from '@packages/shared/src/ui/Radio/Radio';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';

import { extractDisplayMode, extractOrder, extractSelectedFilterValues } from '../../queryEncoding';
import { CATEGORY_DIALOG_PARAM } from '../FilterSettingsMobileV2/useNestedFilterDialogsV2';
import { productCategoryNavigationMobileV2Messages as messages } from './productCategoryNavigationMobileV2.messages';

/* GraphQL */ `
  fragment UseCategoryDataForProductCategoryNavigationMobileV2Fragment on FlatCategoryItem {
    id
    name
    pathName
    level
    isSelectedExact
    isSelectedIndirectly
    parentId
    hasChildren
  }
`;

/* GraphQL */ `
  fragment UseCategoryDataForProductCategoryNavigationMobileWorkaroundV2Fragment on CategoryResult {
    currentTopLevelCategory {
      ...UseCategoryDataForProductCategoryNavigationMobileV2Fragment
    }
  }
`;

/* GraphQL */ `
  fragment ProductCategoryNavigationMobileV2Fragment on FlatCategoryItem {
    ...UseCategoryDataForProductCategoryNavigationMobileV2Fragment
  }
`;

/* GraphQL */ `
  fragment ProductCategoryNavigationMobileWorkaroundV2Fragment on CategoryResult {
    ...UseCategoryDataForProductCategoryNavigationMobileWorkaroundV2Fragment
  }
`;

type FlatItem = {
  id: string;
  name: string;
  pathName: string;
  level: number;
  isSelectedExact: boolean | null;
  isSelectedIndirectly: boolean | null;
  parentId: string | null;
  hasChildren: boolean | null;
};

export type ProductCategoryNavigationMobileV2Props = {
  maskedItems: Array<FragmentType<typeof ProductCategoryNavigationMobileV2FragmentFragmentDoc>>;
  maskedResult: FragmentType<typeof ProductCategoryNavigationMobileWorkaroundV2FragmentFragmentDoc>;
  onNavigate?: () => void;
};

export const ProductCategoryNavigationMobileV2 = ({
  maskedItems,
  maskedResult,
  onNavigate,
}: ProductCategoryNavigationMobileV2Props) => {
  const { formatMessage } = useIntl();
  const searchParams = useSearchParams();
  const push = useRouterPush();

  const { items, topLevel } = useMemo(() => {
    const flatItems = maskedItems.map((item) =>
      unmask<typeof UseCategoryDataForProductCategoryNavigationMobileV2FragmentFragmentDoc>(
        item as FragmentType<
          typeof UseCategoryDataForProductCategoryNavigationMobileV2FragmentFragmentDoc
        >,
      ),
    ) as FlatItem[];

    const result = unmask<
      typeof UseCategoryDataForProductCategoryNavigationMobileWorkaroundV2FragmentFragmentDoc
    >(
      maskedResult as FragmentType<
        typeof UseCategoryDataForProductCategoryNavigationMobileWorkaroundV2FragmentFragmentDoc
      >,
    );

    const topLevelItem = unmask<
      typeof UseCategoryDataForProductCategoryNavigationMobileV2FragmentFragmentDoc
    >(result.currentTopLevelCategory) as FlatItem;

    return { items: flatItems, topLevel: topLevelItem };
  }, [maskedItems, maskedResult]);

  const activeCategory = items.find((item) => item.isSelectedExact) ?? null;
  const initialViewId = activeCategory?.id ?? topLevel.id;

  const [currentViewId, setCurrentViewId] = useState<string>(initialViewId);
  const [stagedId, setStagedId] = useState<string>(initialViewId);
  const [isLoadingSubcategories, setIsLoadingSubcategories] = useState(false);

  const allItems = useMemo(() => [topLevel, ...items], [topLevel, items]);
  const currentView = allItems.find((item) => item.id === currentViewId) ?? topLevel;
  const parentView = currentView.parentId
    ? (allItems.find((item) => item.id === currentView.parentId) ?? null)
    : null;

  const visibleChildren = items.filter((item) => item.parentId === currentView.id);

  const buildHref = (item: FlatItem, keepDialogOpen: boolean) => {
    const query = searchParamsToObject(searchParams);
    const baseParams: Record<string, string | string[] | undefined> = item.isSelectedIndirectly
      ? {}
      : {
          ...extractDisplayMode(query),
          ...extractOrder(query),
          ...extractSelectedFilterValues(query),
        };
    if (keepDialogOpen) {
      baseParams[CATEGORY_DIALOG_PARAM] = '1';
    }
    return Object.keys(baseParams).length === 0
      ? item.pathName
      : mergeQueryParameters(item.pathName, baseParams);
  };

  const handleDrill = (item: FlatItem) => {
    const hasChildrenInSpine = items.some((other) => other.parentId === item.id);
    if (item.hasChildren && hasChildrenInSpine) {
      setCurrentViewId(item.id);
      setStagedId(item.id);
      return;
    }
    setIsLoadingSubcategories(true);
    push(buildHref(item, true));
  };

  const handleApply = () => {
    const staged = allItems.find((item) => item.id === stagedId);
    onNavigate?.();
    if (staged) {
      push(buildHref(staged, false));
    }
  };

  const showAllCategoriesBack = currentView.id !== topLevel.id;
  const showPreviousCategoryBack = currentView.id !== topLevel.id && parentView !== null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto overscroll-none">
        {(showAllCategoriesBack || showPreviousCategoryBack) && (
          <ul className="bg-color-background-base m-1 list-none rounded-md">
            {showAllCategoriesBack && (
              <li>
                <button
                  type="button"
                  className="text-size-body-sm text-color-text-dark hover:bg-color-grey-light border-b-color-separator flex w-full items-center gap-1 border-b px-1 py-1 text-left"
                  onClick={() => {
                    setCurrentViewId(topLevel.id);
                    setStagedId(topLevel.id);
                  }}
                >
                  <span className="icon-emp-arrow-medium-left size-4" aria-hidden="true" />
                  <span className="font-bold">
                    <FormattedMessage {...messages.allCategories} />
                  </span>
                </button>
              </li>
            )}
            {showPreviousCategoryBack && parentView && (
              <li>
                <button
                  type="button"
                  className="text-size-body-sm text-color-text-dark hover:bg-color-grey-light flex w-full items-center gap-1 px-1 py-1 text-left"
                  onClick={() => {
                    setCurrentViewId(parentView.id);
                    setStagedId(parentView.id);
                  }}
                >
                  <span className="icon-emp-arrow-medium-left size-4" aria-hidden="true" />
                  <FormattedMessage
                    {...messages.previousCategory}
                    values={{ name: parentView.name }}
                  />
                </button>
              </li>
            )}
          </ul>
        )}

        <ul className="bg-color-background-base m-1 list-none rounded-md">
          <li className="border-b-color-primary border-b-4 px-1 py-1">
            <span className="text-size-body-base font-bold">{currentView.name}</span>
          </li>

          <li className="border-b-color-separator border-b px-1 py-1">
            <Radio
              aria-label={formatMessage(messages.allIn, { name: currentView.name })}
              label={formatMessage(messages.allIn, { name: currentView.name })}
              checked={stagedId === currentView.id}
              onChange={() => setStagedId(currentView.id)}
            />
          </li>

          {isLoadingSubcategories
            ? visibleChildren.map((child) => (
                <li
                  key={`skeleton-${child.id}`}
                  className="border-b-color-separator border-b px-1 py-1 last:border-b-0"
                >
                  <Skeleton className="ml-4 h-3 w-3/4" />
                </li>
              ))
            : visibleChildren.map((child, index) => {
                const isLast = index === visibleChildren.length - 1;
                const liClass = isLast
                  ? 'px-1 py-1'
                  : 'px-1 py-1 border-b-color-separator border-b';

                if (child.hasChildren) {
                  return (
                    <li key={child.id} className={liClass}>
                      <button
                        type="button"
                        onClick={() => handleDrill(child)}
                        aria-label={formatMessage(messages.drillIntoCategory, { name: child.name })}
                        className="text-color-text-dark flex w-full items-center justify-between pl-4 text-left"
                      >
                        <span className="text-size-body-sm">{child.name}</span>
                        <span className="icon-emp-arrow-medium-right size-4" aria-hidden="true" />
                      </button>
                    </li>
                  );
                }

                return (
                  <li key={child.id} className={liClass}>
                    <Radio
                      aria-label={child.name}
                      label={child.name}
                      checked={stagedId === child.id}
                      onChange={() => setStagedId(child.id)}
                    />
                  </li>
                );
              })}
        </ul>

        <p className="text-size-body-sm text-color-text-darktransparent px-2 py-1.5">
          <FormattedMessage {...messages.helperSingle} />
        </p>
      </div>

      <div className="bg-color-background-base sticky bottom-0 mt-auto flex flex-col gap-1 p-1 shadow-md">
        <Button color="primary" onClick={handleApply} className="rounded-full">
          <FormattedMessage {...messages.submit} />
        </Button>
        <Button variant="link" onClick={() => onNavigate?.()} className="rounded-full no-underline">
          <FormattedMessage {...messages.cancel} />
        </Button>
      </div>
    </div>
  );
};
